import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { animate, svg } from 'animejs';
import { Navbar } from '../components/layout/Navbar';
import { SiegeTitle } from '../components/siege/SiegeTitle';
import { useAppState } from '../hooks/useAppState';
import { MOCK_PORTFOLIO, PHASE_DEFS } from '../data/siege';

// ─── Pixel-art silver/steel palette ────────────────────────────────────
const C = {
  bg: '#0d1117',
  bgPanel: '#111923',
  stone: '#2a323e',
  stoneLight: '#3d4654',
  silver: '#B8C0CC',
  silverBright: '#E0E4EC',
  silverDim: '#6a7280',
  shimmerPeak: '#FFFFFF',
  moss: '#7aa55c',
  mossDark: '#3d5c2d',
  blood: '#b23838',
  text: '#d8dce4',
  textDim: '#7a828e',
};

const PX_FONT = "'VT323', monospace";
const BLOCK_FONT = "'Press Start 2P', monospace";
const TITLE_FONT = "'Metamorphous', serif";

// ─── Shared shimmer gradient (animated) ────────────────────────────────
function ShimmerGradient({ id }: { id: string }) {
  return (
    <motion.linearGradient id={id} gradientUnits="userSpaceOnUse"
      animate={{ x1: [-40, 340], x2: [-20, 360] }}
      transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 4, ease: 'linear' }}>
      <stop stopColor={C.silver} stopOpacity="0" />
      <stop offset="0.4" stopColor={C.silver} stopOpacity="1" />
      <stop offset="0.5" stopColor={C.shimmerPeak} stopOpacity="1" />
      <stop offset="0.6" stopColor={C.silver} stopOpacity="1" />
      <stop offset="1" stopColor={C.silver} stopOpacity="0" />
    </motion.linearGradient>
  );
}

// ─── Pixel frame (shared panel) ────────────────────────────────────────
function PixelFrame({ title, children, withShimmer = false }: {
  title?: string;
  children: React.ReactNode;
  withShimmer?: boolean;
}) {
  const uid = useRef(`pf-${Math.random().toString(36).slice(2, 7)}`).current;
  return (
    <div style={{ position: 'relative' }}>
      {/* cobblestone texture */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.12, pointerEvents: 'none',
        background: `
          repeating-linear-gradient(0deg, transparent 0 11px, ${C.silver}22 11px 12px),
          repeating-linear-gradient(90deg, transparent 0 24px, ${C.silver}22 24px 25px)
        `,
      }} />
      <div style={{
        position: 'relative',
        background: C.bgPanel,
        border: `2px solid ${C.silver}`,
        padding: '16px 20px',
        boxShadow: `0 0 0 2px ${C.bg}, 0 0 0 4px ${C.stone}, 0 0 24px ${C.silver}22`,
      }}>
        {title && (
          <div style={{
            position: 'absolute', top: -12, left: 20,
            background: C.bg, padding: '2px 12px',
            fontFamily: TITLE_FONT, fontSize: 14,
            color: C.silverBright, letterSpacing: '0.2em',
            textShadow: `0 0 8px ${C.silver}, 0 0 2px ${C.silver}`,
            border: `1px solid ${C.silverDim}`,
          }}>
            {title}
          </div>
        )}
        {withShimmer && (
          <svg style={{
            position: 'absolute', inset: -2,
            width: 'calc(100% + 4px)', height: 'calc(100% + 4px)',
            pointerEvents: 'none',
          }}>
            <defs><ShimmerGradient id={`${uid}-shimmer`} /></defs>
            <rect x="0" y="0" width="100%" height="100%" fill="none"
              stroke={`url(#${uid}-shimmer)`} strokeWidth="2" />
          </svg>
        )}
        {children}
      </div>
    </div>
  );
}

// ─── Pixel rook portrait ──────────────────────────────────────────────
function PixelRook({ size = 48 }: { size?: number }) {
  const pix = (x: number, y: number, w: number, h: number, fill: string) =>
    <rect x={x} y={y} width={w} height={h} fill={fill} shapeRendering="crispEdges" />;
  return (
    <svg width={size} height={size * 1.25} viewBox="0 0 16 20" shapeRendering="crispEdges">
      {pix(2, 0, 2, 2, C.silver)}{pix(6, 0, 2, 2, C.silver)}
      {pix(10, 0, 2, 2, C.silver)}{pix(12, 0, 2, 2, C.silver)}
      {pix(1, 2, 14, 2, C.silver)}{pix(2, 4, 12, 1, C.stoneLight)}
      {pix(3, 5, 10, 1, C.silver)}{pix(3, 6, 10, 8, C.silver)}
      {pix(3, 9, 10, 1, C.stoneLight)}{pix(2, 14, 12, 1, C.silver)}
      {pix(1, 15, 14, 2, C.silver)}{pix(0, 17, 16, 3, C.stoneLight)}
      {pix(0, 19, 16, 1, C.stone)}
    </svg>
  );
}

// ─── Hero (character) Card ─────────────────────────────────────────────
function HeroCard({ userName, phase, tier }: { userName: string; phase: number; tier: string }) {
  return (
    <PixelFrame title="HERO">
      <div className="flex items-center gap-4">
        <div style={{
          width: 64, height: 76, background: C.stone,
          border: `2px solid ${C.silver}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `inset 0 0 12px ${C.silver}22`,
        }}>
          <PixelRook size={42} />
        </div>
        <div className="flex flex-col gap-1">
          <div style={{
            fontFamily: TITLE_FONT, fontSize: 22, color: C.silverBright,
            textShadow: `0 0 8px ${C.silver}AA, 0 0 2px ${C.silver}`,
          }}>
            {userName.toUpperCase()}
          </div>
          <div style={{ fontFamily: PX_FONT, fontSize: 20, color: C.text, lineHeight: 1 }}>
            Lv.{phase} · {tier.toUpperCase()} TIER
          </div>
          <div style={{ fontFamily: PX_FONT, fontSize: 16, color: C.textDim }}>
            PHASE {phase} / 7
          </div>
        </div>
      </div>
    </PixelFrame>
  );
}

// ─── Daily Orders ──────────────────────────────────────────────────────
function DailyOrders({ tier, currentPhase, accountsTotal, gatesPending }: {
  tier: string; currentPhase: number; accountsTotal: number; gatesPending: number;
}) {
  return (
    <PixelFrame title="DAILY ORDERS">
      <div style={{ fontFamily: PX_FONT, fontSize: 20, color: C.text, lineHeight: 1.3 }}>
        ⚔ Hold {tier.toUpperCase()} tier through Phase {currentPhase}.<br />
        ⚔ Complete {gatesPending} gate{gatesPending === 1 ? '' : 's'} to advance.<br />
        ⚔ {accountsTotal} accounts deployed.
      </div>
    </PixelFrame>
  );
}

// ─── Campaign Map (Skill Tree) ────────────────────────────────────────
interface SkillTreeProps {
  currentPhase: number;
  cohorts: Array<{ id: string; label: string; clusterId?: number }>;
  accounts: Array<{ id: string; firmAbbr: string; size: number; state: string; cohortId: string; isMaster: boolean }>;
}
function CampaignMap({ currentPhase, cohorts, accounts }: SkillTreeProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [revealed, setRevealed] = useState(false);
  const uid = useRef(`st-${Math.random().toString(36).slice(2, 7)}`).current;
  const pulseId = `${uid}-pulse`;

  const phases = [
    ...PHASE_DEFS.map(p => ({
      id: p.phase,
      tier: p.tier.toUpperCase(),
      accounts: p.accounts,
      status: p.phase < currentPhase ? 'done' as const
        : p.phase === currentPhase ? 'current' as const
        : 'locked' as const,
    })),
    // Endgame phase 7
    { id: 7, tier: 'HAMMER', accounts: 28, status: 'locked' as const },
  ];

  const nodeR = 18;
  const phaseY = 50;
  const phaseSpacing = 90;
  const startX = 60;
  const clusterY = 180;
  const cohortY = 270;
  const accountY = 340;
  const currentIdx = phases.findIndex(p => p.status === 'current');
  const currentX = startX + currentIdx * phaseSpacing;

  // Build clusters from real data (phase 5 has 2 clusters; other phases have cohorts grouped differently)
  const cluster1Cohorts = cohorts.filter(c => !c.clusterId || c.clusterId === 1).slice(0, 3);
  const cluster2Cohorts = cohorts.filter(c => c.clusterId === 2).slice(0, 3);
  const hasDualCluster = cluster2Cohorts.length > 0;

  const renderCohorts = hasDualCluster
    ? [{ label: 'CL-1', cohorts: cluster1Cohorts }, { label: 'CL-2', cohorts: cluster2Cohorts }]
    : [{ label: 'CL-1', cohorts: cluster1Cohorts }];

  const cluster1X = hasDualCluster ? currentX - 110 : currentX;
  const cluster2X = currentX + 110;
  const clusterXs = hasDualCluster ? [cluster1X, cluster2X] : [cluster1X];

  useEffect(() => {
    if (!svgRef.current) return;
    const paths = svgRef.current.querySelectorAll(`#${uid}-traceLayer path, #${uid}-traceLayer line`);
    if (paths.length === 0) return;
    const drawables = svg.createDrawable(paths as NodeListOf<SVGElement>);
    animate(drawables, {
      draw: ['0 0', '0 1'], ease: 'inOutQuad', duration: 2000,
      delay: (_el: any, i: number) => i * 30,
      onComplete: () => setRevealed(true),
    });
  }, [uid]);

  const statusColor = (s: string) => s === 'done' ? C.moss : s === 'current' ? C.silverBright : C.silverDim;

  const phaseLinks = phases.slice(0, -1).map((p, i) => {
    const x1 = startX + i * phaseSpacing + nodeR;
    const x2 = startX + (i + 1) * phaseSpacing - nodeR;
    const isPast = p.status === 'done' && phases[i + 1].status !== 'locked';
    return { d: `M${x1} ${phaseY} L${x2} ${phaseY}`, isPast, key: `pl-${i}` };
  });

  return (
    <svg ref={svgRef} viewBox="0 0 700 400" width="100%" height="440" shapeRendering="crispEdges">
      <defs><ShimmerGradient id={pulseId} /></defs>

      {/* phase connections */}
      {phaseLinks.map(link => (
        <path key={link.key} d={link.d}
          stroke={link.isPast ? C.moss : C.silverDim} strokeWidth={3} fill="none" />
      ))}

      {/* phase nodes */}
      {phases.map((p, i) => {
        const x = startX + i * phaseSpacing;
        const color = statusColor(p.status);
        const isCurrent = p.status === 'current';
        return (
          <g key={p.id}>
            <rect x={x - nodeR - 2} y={phaseY - nodeR - 2} width={(nodeR + 2) * 2} height={(nodeR + 2) * 2}
              fill={C.bg} stroke={color} strokeWidth={isCurrent ? 3 : 2} />
            <rect x={x - nodeR + 3} y={phaseY - nodeR + 3} width={(nodeR - 3) * 2} height={(nodeR - 3) * 2} fill={C.stone} />
            {isCurrent && revealed && (
              <rect x={x - nodeR - 2} y={phaseY - nodeR - 2} width={(nodeR + 2) * 2} height={(nodeR + 2) * 2}
                fill="none" stroke={`url(#${pulseId})`} strokeWidth={3} />
            )}
            <text x={x} y={phaseY + 5} textAnchor="middle" fontFamily={BLOCK_FONT} fontSize={12} fill={color}>{p.id}</text>
            {p.status === 'done' && (
              <text x={x} y={phaseY + nodeR + 16} textAnchor="middle" fontFamily={PX_FONT} fontSize={14} fill={C.moss}>✓</text>
            )}
            {isCurrent && (
              <motion.text x={x} y={phaseY + nodeR + 16} textAnchor="middle"
                fontFamily={PX_FONT} fontSize={16} fill={C.silverBright}
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1.5, repeat: Infinity }}>▲</motion.text>
            )}
            {p.status === 'locked' && (
              <text x={x} y={phaseY + nodeR + 16} textAnchor="middle" fontFamily={PX_FONT} fontSize={14} fill={C.silverDim}>✕</text>
            )}
            <text x={x} y={phaseY + nodeR + 30} textAnchor="middle" fontFamily={BLOCK_FONT} fontSize={6} fill={C.textDim} letterSpacing="0.15em">{p.tier}</text>
            <text x={x} y={phaseY + nodeR + 46} textAnchor="middle" fontFamily={PX_FONT} fontSize={14} fill={C.text}>{p.accounts} ACC</text>
          </g>
        );
      })}

      {/* branches from current phase */}
      <g id={`${uid}-branches`} stroke={C.silver} strokeWidth={3} fill="none">
        <line x1={currentX} y1={phaseY + nodeR + 50} x2={currentX} y2={phaseY + nodeR + 90} />
        {hasDualCluster && (
          <>
            <line x1={cluster1X} y1={phaseY + nodeR + 90} x2={cluster2X} y2={phaseY + nodeR + 90} />
            <line x1={cluster1X} y1={phaseY + nodeR + 90} x2={cluster1X} y2={clusterY - 18} />
            <line x1={cluster2X} y1={phaseY + nodeR + 90} x2={cluster2X} y2={clusterY - 18} />
          </>
        )}
        {!hasDualCluster && (
          <line x1={cluster1X} y1={phaseY + nodeR + 90} x2={cluster1X} y2={clusterY - 18} />
        )}
      </g>

      {/* cluster nodes + cohort branches */}
      {clusterXs.map((cx, ci) => {
        const cluster = renderCohorts[ci];
        if (!cluster) return null;
        return (
          <g key={`cl-${ci}`}>
            <rect x={cx - 40} y={clusterY - 16} width={80} height={32}
              fill={C.stone} stroke={C.silver} strokeWidth={2} />
            <rect x={cx - 37} y={clusterY - 13} width={74} height={26}
              fill="none" stroke={C.silverBright} strokeWidth={1} opacity={0.4} />
            <text x={cx} y={clusterY + 5} textAnchor="middle"
              fontFamily={BLOCK_FONT} fontSize={10} fill={C.silverBright}>{cluster.label}</text>

            <g stroke={C.silver} strokeWidth={2} fill="none">
              <line x1={cx} y1={clusterY + 16} x2={cx} y2={clusterY + 46} />
              {cluster.cohorts.slice(0, 3).map((_, coi) => {
                const coX = cx + (coi - 1) * 60;
                return (
                  <g key={coi}>
                    {coi !== 1 && <line x1={cx} y1={clusterY + 46} x2={coX} y2={clusterY + 46} />}
                    <line x1={coX} y1={clusterY + 46} x2={coX} y2={clusterY + 62} />
                  </g>
                );
              })}
            </g>

            {cluster.cohorts.slice(0, 3).map((co, coi) => {
              const coX = cx + (coi - 1) * 60;
              const cohortAccounts = accounts.filter(a => a.cohortId === co.id && !a.isMaster).slice(0, 2);
              return (
                <g key={co.id}>
                  <rect x={coX - 20} y={cohortY - 12} width={40} height={24}
                    fill={C.bgPanel} stroke={C.silver} strokeWidth={1.5} />
                  <text x={coX} y={cohortY + 5} textAnchor="middle"
                    fontFamily={BLOCK_FONT} fontSize={8} fill={C.silver}>{co.label}</text>
                  {cohortAccounts.map((_, ai) => {
                    const aX = coX + (ai - (cohortAccounts.length - 1) / 2) * 28;
                    return <line key={`d-${ai}`} x1={aX} y1={cohortY + 12} x2={aX} y2={accountY - 8} stroke={C.silver} strokeWidth={1.5} />;
                  })}
                  {cohortAccounts.map((acc, ai) => {
                    const aX = coX + (ai - (cohortAccounts.length - 1) / 2) * 28;
                    const accColor = acc.state === 'BLOWN' ? C.blood
                      : acc.state === 'REPAIR' ? C.silverDim : C.silver;
                    return (
                      <g key={ai}>
                        <rect x={aX - 10} y={accountY - 8} width={20} height={16}
                          fill={C.stone} stroke={accColor} strokeWidth={1.5} />
                        <text x={aX} y={accountY + 4} textAnchor="middle"
                          fontFamily={BLOCK_FONT} fontSize={5} fill={accColor}>{acc.firmAbbr}</text>
                      </g>
                    );
                  })}
                </g>
              );
            })}
          </g>
        );
      })}

      {/* shimmer sweep on phase links */}
      {revealed && (
        <g stroke={`url(#${pulseId})`} strokeWidth={2} fill="none">
          {phaseLinks.map(link => <path key={`sh-${link.key}`} d={link.d} />)}
        </g>
      )}

      {/* anime.js trace cover layer */}
      <g id={`${uid}-traceCover`} style={{ opacity: revealed ? 0 : 1, transition: 'opacity 0.7s' }}>
        <rect x="0" y="0" width="700" height="400" fill={C.bg} opacity="0.96" />
        <g id={`${uid}-traceLayer`} stroke={C.silver} strokeWidth={2}
          strokeLinecap="round" strokeLinejoin="round" fill="none">
          {phaseLinks.map(link => <path key={`t-${link.key}`} d={link.d} />)}
        </g>
      </g>

      <text x={350} y={15} textAnchor="middle"
        fontFamily={TITLE_FONT} fontSize={14} fill={C.silver}
        letterSpacing="0.3em" style={{ textShadow: `0 0 6px ${C.silver}AA` }}>
        ── CAMPAIGN MAP ──
      </text>
    </svg>
  );
}

// ─── Pixel XP Bar ─────────────────────────────────────────────────────
function PixelBar({ current, goal, color }: { current: number; goal: number; color: string }) {
  const pct = Math.min(1, current / goal);
  const segments = 20;
  const filled = Math.round(pct * segments);
  return (
    <div className="flex items-center gap-2">
      <div style={{ display: 'flex', gap: 1, padding: 2, background: C.bg, border: `1px solid ${C.stone}` }}>
        {Array.from({ length: segments }).map((_, i) => (
          <div key={i} style={{
            width: 8, height: 14,
            background: i < filled ? color : C.stone,
            boxShadow: i < filled ? `inset -1px -1px 0 rgba(0,0,0,0.3), inset 1px 1px 0 ${C.shimmerPeak}40` : 'none',
          }} />
        ))}
      </div>
      <div style={{ fontFamily: PX_FONT, fontSize: 18, color: C.text }}>{current}/{goal}</div>
    </div>
  );
}

// ─── Quest Log ─────────────────────────────────────────────────────────
function QuestLog({ gate1Payout, gate2DaysClean, gate3TradeCount, gate3Target }: {
  gate1Payout: boolean; gate2DaysClean: number; gate3TradeCount: number; gate3Target: number;
}) {
  const quests = [
    { id: 'payout', name: 'CLAIM THE PAYOUT', status: gate1Payout ? 'complete' as const : 'active' as const,
      current: gate1Payout ? 1 : 0, goal: 1 },
    { id: 'clean', name: 'CLEAN WINDOW', status: (gate2DaysClean >= 30 ? 'complete' : 'active') as 'complete' | 'active',
      current: gate2DaysClean, goal: 30 },
    { id: 'volume', name: 'TRADE VOLUME', status: (gate3TradeCount >= gate3Target ? 'complete' : 'active') as 'complete' | 'active',
      current: gate3TradeCount, goal: gate3Target },
  ];
  return (
    <PixelFrame title="QUEST LOG">
      <div className="flex flex-col gap-3">
        {quests.map(q => (
          <div key={q.id} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span style={{
                fontFamily: PX_FONT, fontSize: 22,
                color: q.status === 'complete' ? C.moss : C.silverBright,
                textShadow: q.status !== 'complete' ? `0 0 6px ${C.silver}` : 'none',
              }}>
                {q.status === 'complete' ? '✓' : '⚔'}
              </span>
              <div>
                <div style={{
                  fontFamily: TITLE_FONT, fontSize: 16,
                  color: q.status === 'complete' ? C.moss : C.text,
                  letterSpacing: '0.08em',
                }}>{q.name}</div>
                <div style={{ fontFamily: PX_FONT, fontSize: 14, color: C.textDim }}>
                  {q.status === 'complete' ? 'REWARD CLAIMED' : 'IN PROGRESS'}
                </div>
              </div>
            </div>
            <PixelBar current={q.current} goal={q.goal}
              color={q.status === 'complete' ? C.moss : C.silver} />
          </div>
        ))}
      </div>
    </PixelFrame>
  );
}

// ─── Inventory ─────────────────────────────────────────────────────────
function Inventory({ accounts }: { accounts: Array<{ id: string; firmAbbr: string; size: number; state: string; cohortId: string; isMaster: boolean }> }) {
  return (
    <PixelFrame title="INVENTORY">
      <div className="grid grid-cols-7 gap-2">
        {accounts.map((acc, i) => {
          const accColor = acc.isMaster ? C.silverBright
            : acc.state === 'BLOWN' ? C.blood
            : acc.state === 'REPAIR' ? C.silverDim : C.silver;
          return (
            <div key={i} style={{
              padding: 8, background: C.stone,
              border: `2px solid ${accColor}`,
              boxShadow: `inset 1px 1px 0 ${C.stoneLight}, inset -1px -1px 0 ${C.bg}, 0 0 8px ${accColor}33`,
            }}>
              <div style={{ fontFamily: BLOCK_FONT, fontSize: 8, color: accColor, textAlign: 'center' }}>
                {acc.firmAbbr}
              </div>
              <div style={{ fontFamily: PX_FONT, fontSize: 14, color: C.text, textAlign: 'center', marginTop: 2 }}>
                ${acc.size >= 1000 ? `${acc.size / 1000}K` : acc.size}
              </div>
              <div style={{ fontFamily: PX_FONT, fontSize: 12, color: C.textDim, textAlign: 'center' }}>
                {acc.cohortId.replace('master-', 'M').replace('cohort-', '')}
              </div>
            </div>
          );
        })}
      </div>
    </PixelFrame>
  );
}

// ─── Dashboard Page ────────────────────────────────────────────────────
export function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAppState();

  if (!user) {
    navigate('/');
    return null;
  }

  const portfolio = MOCK_PORTFOLIO;
  const phaseDef = PHASE_DEFS.find(p => p.phase === portfolio.currentPhase)!;

  const gatesPending = [
    portfolio.gate1Payout,
    portfolio.gate2DaysClean >= 30,
    portfolio.gate3TradeCount >= portfolio.gate3Target,
  ].filter(x => !x).length;

  return (
    <>
      <Navbar />
      <main style={{
        minHeight: '100vh',
        background: `
          radial-gradient(ellipse at top, ${C.silver}12 0%, transparent 50%),
          repeating-linear-gradient(45deg, ${C.bg} 0px, ${C.bg} 2px, #121820 2px, #121820 4px)
        `,
        color: C.text,
        paddingTop: 64,
        fontFamily: PX_FONT,
      }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 32px 48px' }}>

          {/* SIEGE TITLE — pixel-brick castle + plaque */}
          <div className="mb-8">
            <SiegeTitle />
          </div>

          {/* HERO + DAILY ORDERS */}
          <div className="grid grid-cols-[auto_1fr] gap-6 mb-8">
            <HeroCard
              userName={user.discordUsername || 'Paladin'}
              phase={portfolio.currentPhase}
              tier={phaseDef.tier}
            />
            <DailyOrders
              tier={phaseDef.tier}
              currentPhase={portfolio.currentPhase}
              accountsTotal={portfolio.accounts.length}
              gatesPending={gatesPending}
            />
          </div>

          {/* CAMPAIGN MAP */}
          <div className="mb-8">
            <PixelFrame title="CAMPAIGN MAP" withShimmer>
              <CampaignMap
                currentPhase={portfolio.currentPhase}
                cohorts={portfolio.cohorts}
                accounts={portfolio.accounts}
              />
            </PixelFrame>
          </div>

          {/* QUEST LOG */}
          <div className="mb-8">
            <QuestLog
              gate1Payout={portfolio.gate1Payout}
              gate2DaysClean={portfolio.gate2DaysClean}
              gate3TradeCount={portfolio.gate3TradeCount}
              gate3Target={portfolio.gate3Target}
            />
          </div>

          {/* INVENTORY */}
          <div className="mb-8">
            <Inventory accounts={portfolio.accounts} />
          </div>

          {/* ACTIONS */}
          <div className="grid grid-cols-2 gap-3 mb-8">
            <button onClick={() => navigate('/assess')}
              style={{
                padding: '12px 20px', background: C.stone,
                border: `2px solid ${C.silver}`,
                fontFamily: BLOCK_FONT, fontSize: 10, color: C.silverBright,
                letterSpacing: '0.15em', cursor: 'pointer',
                boxShadow: `0 0 0 2px ${C.bg}, 0 0 0 4px ${C.stoneLight}`,
              }}>
              + NEW PROTOCOL
            </button>
            <button onClick={() => navigate('/stats/backtest')}
              style={{
                padding: '12px 20px', background: C.stone,
                border: `2px solid ${C.silver}`,
                fontFamily: BLOCK_FONT, fontSize: 10, color: C.silverBright,
                letterSpacing: '0.15em', cursor: 'pointer',
                boxShadow: `0 0 0 2px ${C.bg}, 0 0 0 4px ${C.stoneLight}`,
              }}>
              ↻ UPDATE STATS
            </button>
          </div>

          {/* LEGAL */}
          <div style={{
            fontFamily: PX_FONT, fontSize: 14, color: C.textDim, textAlign: 'center',
            lineHeight: 1.4, marginTop: 24,
          }}>
            Paladin is an educational tool, not investment advice.<br />
            The Siege System is a portfolio management framework — not a signal service.
          </div>

          {/* FOOTER */}
          <div style={{
            textAlign: 'center', fontFamily: TITLE_FONT, fontSize: 12,
            color: C.textDim, letterSpacing: '0.3em', marginTop: 24,
          }}>
            ═══ HONOR THY SETUP ═══
          </div>

        </div>
      </main>
    </>
  );
}
